import React, { useState } from 'react';
import api from '../../services/api';
import Button from '../ui/Button';
import Input from '../ui/Input';

const ScheduleMeetingForm = ({ chamaId, onSuccess, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    scheduled_at: '',
    scheduled_time: '',
    location: '',
    agenda: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setErrors({});

      // Combine date and time
      const scheduledDateTime = `${formData.scheduled_at} ${formData.scheduled_time}:00`;

      await api.post('/meetings', {
        chama_id: chamaId,
        title: formData.title,
        description: formData.description,
        meeting_date: scheduledDateTime,
        location: formData.location,
        agenda: formData.agenda,
      });

      onSuccess?.();
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: 'Failed to schedule meeting' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {errors.general && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-800">
          {errors.general}
        </div>
      )}

      <Input
        label="Meeting Title"
        name="title"
        value={formData.title}
        onChange={handleChange}
        error={errors.title?.[0]}
        placeholder="e.g., Monthly Review Meeting"
        required
      />

      <div>
        <label
          htmlFor="meeting-description"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Description
        </label>
        <textarea
          id="meeting-description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={3}
          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all resize-none"
          placeholder="What will be discussed?"
        />
        {errors.description && <p className="mt-2 text-sm text-red-600">{errors.description[0]}</p>}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Date"
          name="scheduled_at"
          type="date"
          value={formData.scheduled_at}
          onChange={handleChange}
          error={errors.meeting_date?.[0]}
          min={new Date().toISOString().split('T')[0]}
          required
        />

        <Input
          label="Time"
          name="scheduled_time"
          type="time"
          value={formData.scheduled_time}
          onChange={handleChange}
          error={errors.scheduled_time?.[0]}
          required
        />
      </div>

      <Input
        label="Location"
        name="location"
        value={formData.location}
        onChange={handleChange}
        error={errors.location?.[0]}
        placeholder="Meeting venue or online link"
        leftIcon={
          <svg
            className="w-5 h-5 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
        }
      />

      <div>
        <label htmlFor="meeting-agenda" className="block text-sm font-medium text-gray-700 mb-2">
          Agenda
        </label>
        <textarea
          id="meeting-agenda"
          name="agenda"
          value={formData.agenda}
          onChange={handleChange}
          rows={4}
          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all resize-none"
          placeholder="List the topics to be discussed..."
        />
        {errors.agenda && <p className="mt-2 text-sm text-red-600">{errors.agenda[0]}</p>}
        <p className="mt-1 text-xs text-gray-500">Optional: Separate items with bullet points</p>
      </div>

      <div className="bg-sky-50 border border-sky-200 rounded-xl p-4">
        <div className="flex gap-3">
          <svg
            className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div className="text-sm text-sky-800">
            <p className="font-semibold mb-1">Meeting Reminders</p>
            <p className="text-xs">
              All members will receive email and SMS notifications about this meeting 24 hours
              before it starts.
            </p>
          </div>
        </div>
      </div>

      <div className="flex gap-4 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} fullWidth disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" fullWidth loading={loading}>
          Schedule Meeting
        </Button>
      </div>
    </form>
  );
};

export default ScheduleMeetingForm;
